import { useCallback, useEffect, useMemo, useState } from 'react'
import { ReactFlowProvider, useReactFlow } from '@xyflow/react'
import { Canvas, type ZoomApi, type CanvasTool } from '@genieartinc/art-canvas'
import { ArtCanvasFrameSchema, type ArtCanvasFrame } from '@genieartinc/schemas/graph'
import { useStudio } from './StudioContext'
import '@genieartinc/art-canvas/styles.css'

/** A host adapter for the existing desktop renderer. App chrome stays in CanvasPage. */
function BoardCanvasInner({ onZoomApi, onZoom, tool }: { tool?: CanvasTool; onZoomApi?: (api: ZoomApi | null) => void; onZoom?: (zoom: number) => void }) {
  const studio = useStudio()
  const flow = useReactFlow()
  const [assets, setAssets] = useState<Record<string, { image?: string; error?: string }>>({})
  const [error, setError] = useState<string | null>(null)
  const frames = studio.project?.artboards
  const projectId = studio.project?.id
  useEffect(() => { setAssets({}); setError(null) }, [projectId])
  useEffect(() => {
    let cancelled = false
    // Resolve visible versions first. History remains selectable without downloading
    // every previous full-resolution artwork as soon as a project is opened.
    const ids = [...new Set((frames ?? []).flatMap(frame => {
      const id = frame.versions[frame.activeVersion]?.assetId
      return id ? [id] : []
    }))]
    let next = 0
    const worker = async () => {
      while (!cancelled && next < ids.length) {
        const id = ids[next++]
        try {
          const image = await studio.resolveAsset(id)
          if (!cancelled) setAssets(current => current[id]?.image === image ? current : { ...current, [id]: { image } })
        } catch (cause) {
          if (!cancelled) setAssets(current => ({ ...current, [id]: { error: (cause as Error).message } }))
        }
      }
    }
    for (let i = 0; i < Math.min(4, ids.length); i++) void worker()
    return () => { cancelled = true }
  }, [frames, studio.resolveAsset])
  const artboards = useMemo<ArtCanvasFrame[]>(() => (frames ?? []).map(frame => ArtCanvasFrameSchema.parse({
    ...frame,
    params: ArtCanvasFrameSchema.shape.params.catch({}).parse(frame.params),
    job: undefined,
    versions: frame.versions.map(version => ({
      id: version.id, label: version.label, gradient: 'var(--muted)',
      ...(version.assetId ? assets[version.assetId] : {}),
      mediaType: studio.project?.assets.find(asset => asset.id === version.assetId)?.mediaType,
    })),
  })), [frames, assets, studio.project?.assets])
  useEffect(() => {
    const id = studio.focusFrameId
    if (!id) return
    const timer = window.setTimeout(() => { void flow.fitView({ nodes: [{ id }], padding: 0.6, duration: 200 }) }, 100)
    return () => window.clearTimeout(timer)
  }, [studio.focusFrameId, flow])
  const move = useCallback((positions: { id: string; x: number; y: number }[]) => {
    void studio.patchFrames(positions.map(({ id, x, y }) => ({ id, patch: { x, y } }))).catch(cause => setError(cause.message))
  }, [studio.patchFrames])
  const pick = useCallback((id: string, activeVersion: number) => {
    void studio.patchFrames([{ id, patch: { activeVersion } }]).catch(cause => setError(cause.message))
  }, [studio.patchFrames])
  const resize = useCallback((id: string, w: number, h: number) => {
    void studio.patchFrames([{ id, patch: { w, h } }]).catch(cause => setError(cause.message))
  }, [studio.patchFrames])
  const text = useCallback((id: string, text: string) => {
    const frame = frames?.find(frame => frame.id === id)
    if (frame) void studio.patchFrames([{ id, patch: { params: { ...frame.params, text } } }]).catch(cause => setError(cause.message))
  }, [frames, studio.patchFrames])
  return <div className="h-full w-full bg-background" aria-label="Artboard canvas">
    <Canvas tool={tool} canvasId={projectId ?? 'loading'} mode="frame" artboards={artboards}
      selectedId={studio.selectedFrameId} onSelect={studio.selectFrame}
      onPickVersion={pick} onMoveFrames={move} onResize={resize} onText={text}
      onZoomApi={onZoomApi} onZoom={onZoom} backgroundColor="var(--border)" readOnly={studio.busy} />
    {!frames?.length && <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">Drop an image to start your Artboard canvas.</div>}
    {(error || studio.error) && <div role="alert" className="absolute bottom-12 left-1/2 max-w-lg -translate-x-1/2 rounded-lg border border-border bg-popover p-3 text-sm text-destructive">{error || studio.error}<button type="button" aria-label="Dismiss error" className="ml-3" onClick={() => setError(null)}>×</button></div>}
  </div>
}
export function StudioBoardCanvas(props: { tool?: CanvasTool; onZoomApi?: (api: ZoomApi | null) => void; onZoom?: (zoom: number) => void }) {
  return <ReactFlowProvider><BoardCanvasInner {...props} /></ReactFlowProvider>
}
